// src/components/PendenzMembersModal.jsx
// Ordnet Team-Mitglieder einem Projekt zu (wie bei den Terminplänen). Nutzt dieselbe
// Zuordnung wie das Controlling (projectEmployeeAssignments). Wer zugeordnet ist, darf
// die Pendenzenliste des Projekts bearbeiten.
import React, { useState, useEffect, useMemo } from "react";
import { getAllTeamMembers } from "../services/azureTeamService";
import {
  getProjectAssignments,
  addProjectMember,
  removeProjectMember,
} from "../services/azureProjectAssignmentService";
// Eigene Styles mitbringen: das Modal wird auch aus Ansichten ohne Pendenzen-Kontext
// geöffnet (Kostenschätzung, Reiter "Website").
import "../styles/pendenzen.scss";

const PendenzMembersModal = ({ projectCode, projectTitle, onClose, onChanged }) => {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [original, setOriginal] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const today = new Date().toISOString().slice(0, 10);
  const isActive = (m) =>
    m.employeeUuid &&
    m.imTeam !== false &&
    m.kategorie !== "Freelancer" &&
    (!m.start || m.start <= today) &&
    (!m.ende || m.ende >= today);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [team, assignments] = await Promise.all([
          getAllTeamMembers(),
          getProjectAssignments(projectCode),
        ]);
        if (cancelled) return;
        const active = team
          .filter(isActive)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const assignedUuids = new Set(
          assignments.map((a) => a.employeeUuid).filter(Boolean)
        );
        setMembers(active);
        setSelected(new Set(assignedUuids));
        setOriginal(new Set(assignedUuids));
      } catch (err) {
        if (!cancelled) setError(err?.message || "Team konnte nicht geladen werden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectCode]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? members.filter((m) => (m.name || "").toLowerCase().includes(q)) : members;
  }, [members, search]);

  const toggle = (uuid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const toAdd = [...selected].filter((u) => !original.has(u));
      const toRemove = [...original].filter((u) => !selected.has(u));
      for (const uuid of toAdd) await addProjectMember(projectCode, uuid);
      for (const uuid of toRemove) await removeProjectMember(projectCode, uuid);
      onChanged?.([...selected]);
      onClose();
    } catch (err) {
      setError(err?.message || "Die Zuordnung konnte nicht gespeichert werden.");
      setSaving(false);
    }
  };

  // Weder Klick auf den Hintergrund noch Escape schliessen (Nutzerwunsch, ADR
  // 0060): kein versehentliches Verwerfen ungespeicherter Eingaben.
  return (
    <div
      className="pendenzen__modal-overlay"
      onKeyDown={(e) => {
        if (e.key === "Escape") e.stopPropagation();
      }}
    >
      <div className="pendenzen__modal pendenzen__modal--share" onClick={(e) => e.stopPropagation()}>
        <div className="pendenzen__modal-header">
          <h3>Personen zuordnen{projectTitle ? ` – ${projectTitle}` : ""}</h3>
          <button type="button" className="pendenzen__modal-close" onClick={onClose} aria-label="Schliessen">
            ×
          </button>
        </div>
        <div className="pendenzen__modal-body">
          <p className="pendenzen__subtitle" style={{ marginBottom: "12px" }}>
            Zugeordnete Personen dürfen die projektbezogenen Inhalte bearbeiten –
            Pendenzen, Termine, Controlling und Kostenschätzung. Der Zugriff ist über
            alle diese Werkzeuge synchron.
          </p>
          {error && (
            <div className="pendenzen__error" role="alert" onClick={() => setError(null)}>
              {error}
            </div>
          )}
          {loading ? (
            <div className="pendenzen__share-empty">Team wird geladen…</div>
          ) : (
            <>
              <input
                type="search"
                className="pendenzen__search"
                placeholder="Person suchen…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ marginBottom: "10px", width: "100%" }}
              />
              <div className="pendenzen__member-list">
                {filtered.length === 0 ? (
                  <div className="pendenzen__share-empty">Keine Person gefunden.</div>
                ) : (
                  filtered.map((m) => (
                    <label key={m.employeeUuid} className="pendenzen__member-item">
                      <input
                        type="checkbox"
                        checked={selected.has(m.employeeUuid)}
                        onChange={() => toggle(m.employeeUuid)}
                      />
                      <span>{m.name || "(ohne Name)"}</span>
                    </label>
                  ))
                )}
              </div>
              <div className="pendenzen__member-count">
                {selected.size} {selected.size === 1 ? "Person" : "Personen"} zugeordnet
              </div>
            </>
          )}
        </div>
        <div className="pendenzen__modal-footer">
          <div className="pendenzen__modal-footer-right">
            <button type="button" className="pendenzen__btn" onClick={onClose} disabled={saving}>
              Abbrechen
            </button>
            <button
              type="button"
              className="pendenzen__btn pendenzen__btn--primary"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? "Speichern…" : "Speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendenzMembersModal;
